import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  MIN_SCORE_MAX,
  MIN_SCORE_STEP,
  SIZE_STEPS,
  sizeAt,
  stepOf,
  type HoldFilter,
} from './hold-filter';

interface HoldFilterPanelProps {
  filter: HoldFilter;
  /** Every step of a drag. */
  onChange: (filter: HoldFilter) => void;
  /** The confidence let go of — what the page saves on the scan, where it may. */
  onConfidenceCommit: (minScore: number | null) => void;
  /** The lowest the confidence goes: the lowest score on the wall. Null: nothing is scored. */
  confidenceFloor: number | null;
  /** The wall's smallest and largest hold. Null: nothing to size. */
  sizeRange: { min: number; max: number } | null;
  hidden: number;
  total: number;
  saving?: boolean;
}

/**
 * **Which of the scan's holds the wall offers**, opened from the bar under the
 * photo: the two lines of `hold-filter.ts`, as two sliders. Whatever falls under
 * either is hidden on the spot, and comes back as it is moved back.
 *
 * The confidence slider is the gym panel's (`ScanWallTools`), down to its
 * floor, its steps and saying how many are hidden: the junk a detector finds —
 * a skylight, the ceiling — scores low. The size slider is for what scores well
 * and is no use to this climb: chalk, a bolt hole, the tiniest foot chips.
 */
export function HoldFilterPanel({
  filter,
  onChange,
  onConfidenceCommit,
  confidenceFloor,
  sizeRange,
  hidden,
  total,
  saving = false,
}: HoldFilterPanelProps) {
  const { t } = useTranslation();
  const percent = (score: number) => `${Math.round(score * 100)}%`;
  // At the floor nothing is hidden, and that is "no line" rather than one.
  const asThreshold = (raw: number) =>
    confidenceFloor == null || raw <= confidenceFloor + MIN_SCORE_STEP / 2 ? null : raw;

  return (
    <div
      className="mx-auto w-full max-w-sm space-y-4 rounded-2xl border bg-background p-3 shadow-sm"
      data-testid="hold-filter-panel"
    >
      {confidenceFloor != null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span id="hold-filter-confidence" className="font-medium">
              {t('climbEditor.holdFilter.confidence')}
            </span>
            <span className="flex items-center gap-1.5 tabular-nums text-muted-foreground">
              {saving && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
              {filter.minScore == null ? t('climbEditor.holdFilter.all') : `≥ ${percent(filter.minScore)}`}
            </span>
          </div>
          <Slider
            aria-labelledby="hold-filter-confidence"
            min={confidenceFloor}
            max={Math.max(MIN_SCORE_MAX, confidenceFloor)}
            step={MIN_SCORE_STEP}
            value={[filter.minScore ?? confidenceFloor]}
            onValueChange={([raw]) => onChange({ ...filter, minScore: asThreshold(raw) })}
            onValueCommit={([raw]) => onConfidenceCommit(asThreshold(raw))}
          />
        </div>
      )}

      {sizeRange && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span id="hold-filter-size" className="font-medium">
              {t('climbEditor.holdFilter.size')}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {filter.minSize == null
                ? t('climbEditor.holdFilter.all')
                : t('climbEditor.holdFilter.sizeValue', { percent: areaPercent(filter.minSize) })}
            </span>
          </div>
          <Slider
            aria-labelledby="hold-filter-size"
            min={0}
            max={SIZE_STEPS}
            step={1}
            value={[stepOf(filter.minSize, sizeRange)]}
            onValueChange={([step]) => onChange({ ...filter, minSize: sizeAt(step, sizeRange) })}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-sm" data-testid="hold-filter-hidden">
          {hidden > 0
            ? t('climbEditor.holdFilter.hidden', { count: hidden, total })
            : t('climbEditor.holdFilter.noneHidden')}
        </p>
        {(filter.minScore != null || filter.minSize != null) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={saving}
            onClick={() => {
              onChange({ minScore: null, minSize: null });
              if (filter.minScore != null) onConfidenceCommit(null);
            }}
          >
            {t('climbEditor.holdFilter.showAll')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** A share of the photo's area, as few digits as say it: 0.012%, 0.5%, 3%. */
function areaPercent(share: number): string {
  return `${Number((share * 100).toPrecision(2))}%`;
}
